import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';

import { BotIcon, NotificationOffIcon } from '@gem/icons';
import { PopoverMenu } from '@gem/ui-kit';

import { AvatarTypes } from 'APP/components/Avatar/Avatar';
import { GroupAvatar } from 'APP/components/Avatar/GroupAvatar/GroupAvatar';
import { SpaceAvatar } from 'APP/components/Avatar/SpaceAvatar/SpaceAvatar';
import { ProfileNameBadge } from 'APP/components/ProfileNameBadge/ProfileNameBadge';
import { SecureChatBadge } from 'APP/components/SecureChatBadge/SecureChatBadge';
import { useContextMenu } from 'APP/main/hooks/useContextMenu/useContextMenu';
import { Group } from 'STORE/Groups/Group';

import { GroupLastDate } from './GroupLastDate/GroupLastDate';
import { useGroupListItemPresenter } from './GroupListItem.presenter';
import styles from './GroupListItem.styles.m.css';
import { GroupStatusIcon } from './GroupStatusIcon/GroupStatusIcon';

export const GROUP_LIST_ITEM_HEIGHT = 64;

interface GroupListItemProps {
  group: Group;
  isShowSpaceIcon?: boolean;
  description?: ReactElement;
  isIconsCentered?: boolean;
  badges?: ReactElement;
}

export const GroupListItem: FC<GroupListItemProps> = observer(
  ({ group, isShowSpaceIcon, description, badges, isIconsCentered }) => {
    const { popover, onContextMenu, modifiers } = useContextMenu<HTMLDivElement>();
    const presenter = useGroupListItemPresenter(group, isShowSpaceIcon, popover);

    const groupContainerClassName = classNames(styles.groupContainer, {
      [styles.focus]: popover.isOpened && presenter.actions.length,
      [styles.active]: presenter.isActive,
    });

    return (
      <>
        <div
          className={groupContainerClassName}
          ref={popover.containerRef}
          role="button"
          tabIndex={0}
          style={{ height: `${GROUP_LIST_ITEM_HEIGHT}px` }}
          data-test="group-link"
          onClick={presenter.onClick}
          onContextMenu={onContextMenu}
        >
          <div className={styles.avatar}>
            <GroupAvatar
              isShowOnlineIndicator
              isShowAutoCleanupIndicator
              group={group}
              type={presenter.avatarType}
              hasLockIcon={presenter.hasAvatarLockIcon}
            />
            {presenter.isShowSpaceIcon && presenter.space && (
              <SpaceAvatar
                className={styles.spaceAvatar}
                space={presenter.space}
                type={AvatarTypes.Square_0}
                isShowLockIcon={false}
              />
            )}
          </div>

          <div className={styles.content}>
            <div className={styles.header}>
              <div className={styles.titleWrapper}>
                {presenter.isShowSecureIcon && <SecureChatBadge />}
                <h6 className={styles.title}>{group.avatarTitle}</h6>
                <ProfileNameBadge
                  verified={presenter.verified}
                  className={styles.badge}
                  source={presenter.userNameBadge?.source}
                  animated={presenter.userNameBadge?.animated}
                  displayUILayer={0}
                />
                {presenter.isShowBotIcon && (
                  <BotIcon className={classNames(styles.icon, styles.iconBot)} />
                )}
                {presenter.isShowMutedIcon && (
                  <NotificationOffIcon className={classNames(styles.icon, styles.iconMuted)} />
                )}
              </div>

              {!isIconsCentered && (
                <>
                  <GroupStatusIcon group={group} />
                  <GroupLastDate
                    date={presenter.lastMessageTs}
                    className={styles.date}
                  />
                </>
              )}
            </div>

            <div className={styles.footer}>
              <div className={styles.description}>{description}</div>

              {!isIconsCentered && <div className={styles.iconPanel}>{badges}</div>}
            </div>
          </div>

          {isIconsCentered && <div className={styles.iconPanel}>{badges}</div>}
        </div>

        <PopoverMenu
          isOpened={popover.isOpened}
          menuItems={presenter.actions}
          onClose={popover.close}
          reference={popover.containerRef.current}
          placement="bottom-start"
          modifiers={modifiers}
        />
      </>
    );
  }
);
